import axios from "axios";
import JwtService from "@/services/jwt";

const state = {
  token: localStorage.getItem("authToken") || null,
  pointOfSaleId: localStorage.getItem("pointOfSaleId") || null,
  loginInProgress: false, // Flag reativa: true enquanto uma tentativa real de login está em andamento
};

// Promise da tentativa de login REAL (POST /auth/proxy-login) em andamento,
// se houver. Fica fora do state porque não precisa ser reativa: serve só
// para que chamadas concorrentes a `login`/`refreshToken` aguardem o MESMO
// resultado (sucesso ou falha) em vez de cada uma decidir por conta própria.
// Escopo de módulo -> uma instância por aba (concorrência entre abas não é
// coberta, assim como não era antes).
let pendingLoginPromise = null;

// integrator//
const actions = {
  /**
   * Garante que exista um token de integrador.
   *
   * payload.force = false (padrão): no-op se já existe um token localmente
   * válido (não expirado pelo `exp` do JWT). Usado por quem só quer
   * "garantir que existe algum token" sem gerar requisição desnecessária.
   *
   * payload.force = true: ignora a validade local e SEMPRE busca um token
   * novo via POST /auth/proxy-login. Usado por quem tem um motivo concreto
   * para desconfiar do token atual (hoje, só `refreshToken`) — por exemplo,
   * um 401 confirmado pelo servidor, que o `exp` local não sabe detectar.
   */
  async login({ commit, state, getters }, { force = false } = {}) {
    if (!force && getters.isTokenValid) {
      return state.token;
    }

    // Já existe uma tentativa real em andamento (desta ou de qualquer outra
    // chamada concorrente). Aguardamos a MESMA promise em vez de uma flag
    // booleana que alguém "libera" e outra chamada confia cegamente: se ela
    // terminar com um token novo, é exatamente o que queríamos; se falhar,
    // propagamos a mesma falha para quem está esperando.
    if (pendingLoginPromise) {
      return pendingLoginPromise;
    }

    state.loginInProgress = true;

    pendingLoginPromise = (async () => {
      try {
        const response = await axios.post("/auth/proxy-login");
        const token = response.data?.response?.token || "";

        if (!token) {
          throw new Error(
            "Token não encontrado na resposta de /auth/proxy-login",
          );
        }

        commit("setToken", token);
        return token;
      } catch (error) {
        console.error(
          "Login failed:",
          error.response ? error.response.data : error.message,
        );
        throw error;
      } finally {
        state.loginInProgress = false;
        pendingLoginPromise = null;
      }
    })();

    return pendingLoginPromise;
  },

  async checkToken({ state, dispatch }) {
    try {
      if (!state.token) {
        await dispatch("login");
        return true;
      }
      if (JwtService.isTokenExpired(state.token)) {
        await dispatch("refreshToken");
        return true;
      }

      const timeUntilExpiration = JwtService.getTimeUntilExpiration(
        state.token,
      );
      if (timeUntilExpiration < 5 * 60 * 1000) {
        // 5 minutos em millisegundos
        await dispatch("refreshToken");
        return true;
      }
      return true; // Token válido e não próximo do vencimento
    } catch (error) {
      // checkToken é "melhor esforço": várias telas fazem
      // `await dispatch("auth/checkToken")` sem try/catch, então esta
      // action nunca deve lançar. Se a renovação falhar aqui, a app segue
      // com o token antigo por mais um instante — a próxima requisição real
      // vai 401 e o interceptor do axios (que propaga falha de verdade)
      // assume dali.
      console.error("checkToken: falha ao garantir token válido:", error);
      return false;
    }
  },

  async refreshToken({ dispatch }) {
    // Única razão de existir de `refreshToken`: o token atual não é
    // confiável (401 confirmado pelo servidor, ou `checkToken` já decidiu
    // que está expirado/perto de expirar). Por isso sempre força — toda a
    // lógica de concorrência já vive em `login`, não duplicamos mutex aqui.
    return dispatch("login", { force: true });
  },

  logout({ commit }) {
    commit("removeToken");
  },

  async apiRequest(
    { state, dispatch },
    { method, url, data, retries = 3, delay = 1000 },
  ) {
    try {
      const config = {
        method,
        url,
        data,
        headers: {
          AuthorizationIntegrator: state.token,
          "Content-Type": "application/json",
        },
      };
      const response = await axios(config);
      return response.data;
    } catch (error) {
      // Se for erro 401, tenta renovar o token
      if (error.response && error.response.status === 401) {
        await dispatch("refreshToken");
        if (retries > 0) {
          return dispatch("apiRequest", {
            method,
            url,
            data,
            retries: retries - 1,
            delay,
          });
        }
      }

      if (retries > 0) {
        console.warn(`Request failed. Retrying in ${delay}ms...`);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return dispatch("apiRequest", {
          method,
          url,
          data,
          retries: retries - 1,
          delay,
        });
      } else {
        console.error(
          "API request failed:",
          error.response ? error.response.data : error.message,
        );
        throw error;
      }
    }
  },
};

const mutations = {
  setToken(state, token) {
    state.token = token;
    if (token) {
      localStorage.setItem("authToken", token);
    } else {
      localStorage.removeItem("authToken");
    }
  },
  removeToken(state) {
    state.token = null;
    localStorage.removeItem("authToken");
    localStorage.removeItem("pointOfSaleId");
  },
  setPointOfSaleId(state, pointOfSaleId) {
    state.pointOfSaleId = pointOfSaleId;
    if (pointOfSaleId) {
      localStorage.setItem("pointOfSaleId", pointOfSaleId);
    }
  },
};

const getters = {
  getToken: (state) => state.token,
  isAuthenticated: (state) =>
    !!state.token && !JwtService.isTokenExpired(state.token),
  isTokenValid: (state) =>
    !!state.token && !JwtService.isTokenExpired(state.token),
  getTokenExpirationTime: (state) =>
    state.token ? JwtService.getTokenExpirationTime(state.token) : null,
  getTimeUntilExpiration: (state) =>
    state.token ? JwtService.getTimeUntilExpiration(state.token) : 0,
  getPointOfSaleId: (state) => {
    return state.pointOfSaleId;
  },
};

export const SET_TOKEN = "SET_TOKEN";
export const REMOVE_TOKEN = "REMOVE_TOKEN";
export const GET_TOKEN = "GET_TOKEN";
export const IS_AUTHENTICATED = "IS_AUTHENTICATED";

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
